/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useRef, useState } from 'react';
import { Grid, makeStyles, Collapse } from '@material-ui/core';
import uniqueId from 'lodash/uniqueId';
import {
  DefaultCard,
  ConfirmationDialog,
  helpers,
  colors,
} from 'design-system';

import FilePresenter from './FilePresenter';
import MessageBoxSpace from '../MessageBoxSpace';
import AddFileButton from './AddFileButton';

const { compressImage, isValidSize, processFileBlob } = helpers;

export type FileUpload = {
  base64String: string;
  fileName: string;
  fileId: string;
  requestStatus: string;
};

type Props = {
  title: string;
  subTitle: string;
  confirmationMessage: string;
  /** It also accepts URL string */
  files: FileUpload[];
  onLocalImageCaptured: (fileName: string, newImage: string) => void;
  onDeleteFile: (fileId: string) => void;
  maxFiles: number;
  disabled?: boolean;
  failUpload?: boolean;
  id?: string;
  deleteButtonId?: string;
};

/**
 * Processes a pdf file and notifies the base64 result
 *
 * @param file - The file captured by the input
 * @param onLocalImageCaptured - Callback called when the processing finishes
 * @param setShowWarning - Setter for the "warning" label
 */
async function processPdf(
  file: File,
  onLocalImageCaptured: (name: string, base64: string) => void,
  setShowWarning: (valid: boolean) => void,
): Promise<void> {
  const result = await processFileBlob(file);
  const valid = isValidSize(file.size);
  setShowWarning(!valid);
  if (valid) {
    onLocalImageCaptured(result.name, result.base64);
  }
}

/**
 * Processes an image file and notifies the base64 result.
 *
 * @param image - The file captured by the input
 * @param onLocalImageCaptured - Callback called when the processing finishes
 * @param setShowWarning - Setter for the "warning" label
 * @param capturerOptions - Custom overwrite capture options for the image compressor
 */
async function processImage(
  image: File,
  onLocalImageCaptured: (name: string, base64: string) => void,
  setShowWarning: (valid: boolean) => void,
): Promise<void> {
  const resizedImage = await compressImage(image);
  onLocalImageCaptured(image.name, resizedImage);
  setShowWarning(false);
}

const useStyles = makeStyles({
  hiddenInput: {
    display: 'none',
  },
  button: {
    marginTop: 5,
  },
  fileContainer: {
    marginTop: 10,
  },
  subtitle: {
    color: colors.madison,
    fontSize: 13,
    fontWeight: 500,
    marginTop: -7,
  },
});

/**
 * PhotoCard will by default check for image mimeTypes. If not image, it will
 * assume it is on pdf format.
 */
const MultiFileLoader = ({
  title,
  subTitle,
  confirmationMessage,
  onLocalImageCaptured,
  onDeleteFile,
  disabled = false,
  id = '',
  deleteButtonId = '',
  files,
  failUpload,
  maxFiles,
}: Props): JSX.Element => {
  const classes = useStyles();
  const uniqueIdRef = useRef<string>(uniqueId('photo_card_')).current;
  const [showWarning, setShowWarning] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedFile, setSelectedFile] = useState('');

  function onDelete(fileId: string): void {
    setSelectedFile(fileId);
    setShowConfirmation(true);
  }

  function handleDeleteConfirm(): void {
    onDeleteFile(selectedFile);
    setShowConfirmation(false);
  }

  function handleDeleteReject(): void {
    setSelectedFile('');
    setShowConfirmation(false);
  }

  const disableButton = disabled || files.length >= maxFiles;

  const uploadButton = useMemo((): JSX.Element => {
    function handleFileInputChange(
      e: React.ChangeEvent<HTMLInputElement>,
    ): void {
      if (!e.target.files || e.target.files.length <= 0) {
        return;
      }
      const firstFile = e.target.files[0];
      // determines mime type. If not pdf, assume by default we are dealing with image.
      if (firstFile.type === 'application/pdf') {
        processPdf(firstFile, onLocalImageCaptured, setShowWarning);
      } else {
        processImage(firstFile, onLocalImageCaptured, setShowWarning);
      }
    }

    return (
      <>
        <input
          data-testid="file-input"
          id={uniqueIdRef}
          type="file"
          onChange={handleFileInputChange}
          accept="image/*, application/pdf"
          className={classes.hiddenInput}
        />
        <label data-testid="local-label" htmlFor={uniqueIdRef}>
          <AddFileButton
            data-testid="take-local-photo-button"
            id={id}
            disabled={disableButton}
          />
        </label>
      </>
    );
  }, [uniqueIdRef, id, disableButton, classes, onLocalImageCaptured]);

  return (
    <Grid container alignContent="stretch">
      <Grid item xs={12}>
        <DefaultCard
          data-testid="card-body"
          disabled={disabled}
          title={title}
          cardStyles={{ textAlign: 'center' }}
        >
          <Grid
            container
            spacing={2}
            justifyContent="center"
            className={classes.subtitle}
          >
            {subTitle}
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            className={classes.fileContainer}
          >
            {files.map(({ base64String, fileId, fileName, requestStatus }) => (
              <Grid item xs={12} lg={6} key={fileId}>
                <FilePresenter
                  data-testid="file-presenter"
                  base64String={base64String}
                  fileId={fileId}
                  fileName={fileName}
                  requestStatus={requestStatus}
                  onDelete={onDelete}
                  deleteButtonId={deleteButtonId}
                  disabled={disabled}
                />
              </Grid>
            ))}
          </Grid>

          <Collapse
            data-testid="warning-card"
            in={showWarning || failUpload}
            style={{ marginTop: 16 }}
          >
            {failUpload ? (
              <MessageBoxSpace
                data-testid="error-message"
                variant="error"
                hideCloseButton
                message="No pudimos subir el archivo. Por favor, intenta nuevamente."
              />
            ) : (
              <MessageBoxSpace
                data-testid="warning-message"
                variant="warning"
                hideCloseButton
                message="El archivo supera el límite de tamaño (5 MB). Por favor, intenta con otro archivo."
              />
            )}
          </Collapse>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            className={classes.button}
          >
            {uploadButton}
          </Grid>
        </DefaultCard>
      </Grid>

      <ConfirmationDialog
        data-testid="confirmation-dialog"
        open={showConfirmation}
        message={confirmationMessage}
        confirmAction={handleDeleteConfirm}
        rejectAction={handleDeleteReject}
      />
    </Grid>
  );
};

export default MultiFileLoader;
